import {
  Link,
  useCreate,
  useDelete,
  useList,
  useUpdate,
  useInvalidate,
} from '@refinedev/core'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  MenuItem,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDebouncedValue } from '../hooks/useDebouncedValue'

interface OrgUsersManagementProps {
  orgUuid: string
}

interface UserOrg {
  user_uuid: string
  org_uuid: string
  role: string
  first_name?: string
  last_name?: string
}

interface UserOrgFormData {
  user_uuid: string
  role: string
}

interface UserSearchOption {
  uuid: string
  first_name?: string
  last_name?: string
  email: string
}

const getUserOptionLabel = (option: UserSearchOption) =>
  option.first_name || option.last_name
    ? `${option.first_name || ''} ${option.last_name || ''} - ${
        option.email
      }`.trim()
    : `No name provided - ${option.email}`

export default function OrgUsersManagement({
  orgUuid,
}: OrgUsersManagementProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [userSearchInput, setUserSearchInput] = useState('')
  const debouncedUserSearch = useDebouncedValue(userSearchInput.trim(), 300)
  const [roleDialogUser, setRoleDialogUser] = useState<UserOrg | null>(null)
  const [roleDialogTarget, setRoleDialogTarget] = useState<'admin' | 'user'>(
    'user',
  )
  // Pending force-retry state. The 409 onError sets this; a useEffect picks
  // it up on the next tick and fires the retry. Decoupling avoids running two
  // back-to-back mutations through the same useUpdate/useDelete hook in the
  // same call stack, which was leaving the cache invalidation behind.
  const [pendingForceRetry, setPendingForceRetry] = useState<
    | { kind: 'delete'; userUuid: string }
    | { kind: 'role'; userUuid: string; role: 'admin' | 'user' }
    | null
  >(null)

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UserOrgFormData>()

  const { data: usersData, isLoading: usersDataIsLoading } =
    useList<UserSearchOption>({
      resource: 'users',
      pagination: { current: 1, pageSize: 25 },
      meta: {
        search: debouncedUserSearch,
      },
      queryOptions: {
        enabled: !!orgUuid,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting users',
          description: error?.message || 'An error occurred while fetching users',
          type: 'error',
        }
      },
      sorters: [{ field: 'first_name', order: 'asc' }],
    })

  const { data: orgUsersData, isLoading: orgUsersDataIsLoading } =
    useList<UserOrg>({
      resource: 'users-orgs',
      meta: {
        org_uuid: orgUuid,
      },
      queryOptions: {
        enabled: !!orgUuid,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting organisation users',
          description:
            error?.message || 'An error occurred while fetching users',
          type: 'error',
        }
      },
    })

  const { mutate: createUserOrg } = useCreate({
    errorNotification: (error) => {
      return {
        message: 'Error adding user to organisation',
        description:
          error?.message ||
          'An error occurred while adding user to organisation',
        type: 'error',
      }
    },
  })

  const { mutate: deleteUserOrg } = useDelete()
  const { mutate: updateUserOrg } = useUpdate()
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()

  // Refine's invalidate occasionally misses the users-orgs list query when
  // two mutations run on the same hook in quick succession (e.g. the
  // force=false 409 followed by the force=true retry). Direct queryClient
  // invalidation is the reliable belt-and-braces.
  const forceRefreshOrgUsers = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey.some((segment) => segment === 'users-orgs'),
    })
  }

  // 409s on the backoffice last-admin guard can be bypassed with force=true.
  // We re-fire the same mutation after the operator confirms.
  const isLastAdmin409 = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as { statusCode?: number; status?: number }
    return e.statusCode === 409 || e.status === 409
  }

  const handleCreate = (data: UserOrgFormData) => {
    createUserOrg(
      {
        resource: 'users-orgs',
        values: {
          user_uuid: data.user_uuid,
          org_uuid: orgUuid,
          role: data.role || 'user',
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'users-orgs',
            invalidates: ['all'],
          })
          forceRefreshOrgUsers()
          handleCloseDialog()
        },
      },
    )
  }

  const fireDelete = (userUuid: string, force: boolean) => {
    deleteUserOrg(
      {
        resource: 'users-orgs',
        id: `${orgUuid}/${userUuid}`,
        meta: force ? { force: true } : undefined,
        errorNotification: (error) => {
          if (isLastAdmin409(error)) return false
          return {
            message: 'Error removing user from organisation',
            description:
              error?.message ||
              'An error occurred while removing user from organisation',
            type: 'error',
          }
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'users-orgs',
            invalidates: ['all'],
          })
          forceRefreshOrgUsers()
        },
        onError: (error) => {
          if (!isLastAdmin409(error) || force) return
          setPendingForceRetry({ kind: 'delete', userUuid })
        },
      },
    )
  }

  const handleDelete = (userUuid: string) => {
    if (
      window.confirm(
        'Are you sure you want to remove this user from the organisation?',
      )
    ) {
      fireDelete(userUuid, false)
    }
  }

  const fireRoleUpdate = (
    userUuid: string,
    role: 'admin' | 'user',
    force: boolean,
  ) => {
    updateUserOrg(
      {
        resource: 'users-orgs',
        id: `${orgUuid}/${userUuid}`,
        values: { role, force },
        errorNotification: (error) => {
          if (isLastAdmin409(error)) return false
          return {
            message: 'Error updating user role',
            description:
              error?.message ||
              'An error occurred while updating the user role',
            type: 'error',
          }
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'users-orgs',
            invalidates: ['all'],
          })
          forceRefreshOrgUsers()
          setRoleDialogUser(null)
        },
        onError: (error) => {
          if (!isLastAdmin409(error) || force) return
          setPendingForceRetry({ kind: 'role', userUuid, role })
        },
      },
    )
  }

  useEffect(() => {
    if (!pendingForceRetry) return
    const retry = pendingForceRetry
    setPendingForceRetry(null)

    const message =
      retry.kind === 'delete'
        ? 'Removing this user would leave the organisation with no admins. Continue anyway?'
        : 'Demoting this user would leave the organisation with no admins. Continue anyway?'

    if (!window.confirm(message)) return

    if (retry.kind === 'delete') {
      fireDelete(retry.userUuid, true)
    } else {
      fireRoleUpdate(retry.userUuid, retry.role, true)
    }
    // fireDelete / fireRoleUpdate are stable inside this component render —
    // including them in deps would re-run the effect every render. We only
    // want to react to pendingForceRetry changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingForceRetry])

  const handleOpenRoleDialog = (userOrg: UserOrg) => {
    setRoleDialogUser(userOrg)
    setRoleDialogTarget(userOrg.role === 'admin' ? 'user' : 'admin')
  }

  const handleCloseRoleDialog = () => {
    setRoleDialogUser(null)
  }

  const handleSubmitRoleUpdate = () => {
    if (!roleDialogUser || roleDialogTarget === roleDialogUser.role) {
      setRoleDialogUser(null)
      return
    }
    fireRoleUpdate(roleDialogUser.user_uuid, roleDialogTarget, false)
  }

  const handleOpenDialog = () => {
    reset({
      user_uuid: '',
      role: 'user',
    })
    setIsDialogOpen(true)
    setUserSearchInput('')
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setUserSearchInput('')
    reset()
  }

  const availableUserOptions = useMemo(() => {
    const currentOrgUsers = new Set(
      (orgUsersData?.data || []).map((userOrg) => userOrg.user_uuid),
    )

    return (usersData?.data || []).filter(
      (user) => !currentOrgUsers.has(user.uuid),
    )
  }, [usersData?.data, orgUsersData?.data])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Organisation Users</Typography>
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Add User
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!orgUsersData || orgUsersDataIsLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Loading users...
                </TableCell>
              </TableRow>
            ) : orgUsersData.data && orgUsersData.data.length > 0 ? (
              orgUsersData.data.map((userOrg) => (
                <TableRow key={userOrg.user_uuid}>
                  <TableCell>
                    <Link
                      to={`/users/${userOrg.user_uuid}`}
                      style={{ color: 'inherit' }}
                    >
                      {userOrg.first_name || userOrg.last_name
                        ? `${userOrg.first_name || ''} ${
                            userOrg.last_name || ''
                          }`.trim()
                        : 'No name provided'}
                    </Link>
                  </TableCell>
                  <TableCell>{userOrg.role}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => handleOpenRoleDialog(userOrg)}
                    >
                      Change Role
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(userOrg.user_uuid)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User to Organisation</DialogTitle>
        <form onSubmit={handleSubmit(handleCreate)}>
          <DialogContent>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="user_uuid"
                control={control}
                rules={{ required: 'User is required' }}
                render={({ field }) => (
                  <Autocomplete
                    loading={usersDataIsLoading}
                    options={availableUserOptions}
                    getOptionLabel={getUserOptionLabel}
                    inputValue={userSearchInput}
                    onInputChange={(_, value, reason) => {
                      if (
                        reason === 'input' ||
                        reason === 'clear' ||
                        reason === 'reset'
                      ) {
                        setUserSearchInput(value)
                      }
                    }}
                    filterOptions={(options) => options}
                    onChange={(_, value) => {
                      field.onChange(value?.uuid)
                      setUserSearchInput(value ? getUserOptionLabel(value) : '')
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User"
                        error={!!errors.user_uuid}
                        helperText={errors.user_uuid?.message}
                      />
                    )}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="role"
                control={control}
                defaultValue="user"
                render={({ field }) => (
                  <TextField select label="Role" {...field}>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ mb: 2, mr: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Add User
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!roleDialogUser}
        onClose={handleCloseRoleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          {roleDialogUser && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Current role: <strong>{roleDialogUser.role}</strong>
              </Typography>
              <FormControl fullWidth>
                <TextField
                  select
                  label="New role"
                  value={roleDialogTarget}
                  onChange={(e) =>
                    setRoleDialogTarget(
                      e.target.value === 'admin' ? 'admin' : 'user',
                    )
                  }
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ mb: 2, mr: 2 }}>
          <Button onClick={handleCloseRoleDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitRoleUpdate}
            disabled={
              !roleDialogUser || roleDialogUser.role === roleDialogTarget
            }
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
